import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { withLatestFrom, tap, concatMap, filter, startWith, pairwise, mapTo } from 'rxjs/operators';
import { State, getSegmentsOptions, getSegmentsConfig } from '../../reducers';
import { select, Store } from '@ngrx/store';
import { ActionTypes as ConfigActionTypes } from '../../actions/invoices/actions-segments-config';
import {
  ActionTypes as OptionsActionTypes,
  Change as OptionsChange
} from '../../actions/invoices/actions-segments-options';
import { Update as ReportUpdate, Reset as ReportReset } from '../../actions/invoices/actions-segments-report';
import { State as SegmentsConfigState } from '../../reducers/invoices/reducers-segments-config';
import { State as SegmentsOptionsState } from '../../reducers/invoices/reducers-segments-options';

@Injectable()
export class EffectsSegments {
  updateOptionsAfterConfig$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ConfigActionTypes.Receive),
      concatMap(_ => this.store.pipe(select(getSegmentsConfig))),
      withLatestFrom(this.store.pipe(select(getSegmentsOptions))),
      filter(
        ([config, options]: [SegmentsConfigState, SegmentsOptionsState]) =>
          config.data.offices && !config.errorMessage && !options.data.offices.length
      ),
      tap(([config, options]: [SegmentsConfigState, SegmentsOptionsState]) => {
        if (config.data.offices.length) {
          this.store.dispatch(
            new OptionsChange({
              options: { ...options.data, offices: config.data.offices },
              apply: true
            })
          );
        } else {
          this.store.dispatch(new ReportReset());
        }
      })
    )
  }, { dispatch: false });

  updateReportAfterOptionsChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OptionsActionTypes.Change),
      startWith(null),
      pairwise<OptionsChange>(),
      filter(([prev, next]) => {
        if (!prev) {
          return false;
        }
        const options = next.payload.options;
        const prevOptions = prev.payload.options;
        const apply = next.payload.apply;
        const valueChanged = Object.entries(options).some(([k, v]) => v !== prevOptions[k]);
        const valid = options.dateFrom && options.dateTo && options.offices.length;
        return (
          (valueChanged && valid && apply) || (!apply && prevOptions.segment !== options.segment) // to update filters in mobile view
        );
      }),
      mapTo(new ReportUpdate())
    )
  });

  updateReportAfterOptionsManuallyApplied$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(OptionsActionTypes.Apply),
      mapTo(new ReportUpdate())
    )
  });

  constructor(private readonly actions$: Actions, private readonly store: Store<State>) { }
}
